import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowRight, Loader2, Wallet, X } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiService } from '@/services/apiService';
import { playClickSound } from '@/utils/soundUtils';
import { Alert, Button, IconButton, Money, StatusBadge, cx, focusRing } from '@/components/ui';
import CancelDepositControl from '@/features/finance/components/CancelDepositControl';
import { DEPOSIT_CANCELLED_EVENT, type DepositCancelledDetail } from '@/utils/depositCancelled';
import { BALANCE_REFRESH_EVENT } from '@/utils/balanceRefresh';
import { getCachedProvidersSync } from '@/features/games/services/gameProviderService';
import {
  canFinishGuidedAdd,
  guidedAddIntentTotal,
  readGuidedAddIntent,
  type GuidedAddIntent,
} from '@/features/money/guidedAddIntent';
import { useOutsidePressDismiss } from '@/features/finance/hooks/useOutsidePressDismiss';
import { EDGE_DOCK_CLASS } from '@/layouts/edgeDock';
import {
  fetchPendingDeposits,
  formatPendingTimeAgo,
  resumePendingDeposit,
  type PendingDeposit,
} from '@/features/finance/hooks/usePendingDeposits';

const USER_ACTION_FETCH_THROTTLE_MS = 30_000;
/** Pages that run a payment themselves (incl. the guided Add money / Cash out): no floater there. */
const PAYMENT_ROUTE_MATCHERS = [
  '/account/finance/payment-checkout',
  '/account/wallet/deposit-checkout',
  '/account/finance/wallet/deposit',
  '/money/add',
  '/money/cash-out',
];

const formatUsd = (amount: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

/** My money available (GET /payment/balance), or null when it cannot be read. */
async function fetchAvailableBalance(): Promise<number | null> {
  const response = await apiService.get<Record<string, unknown>>('/payment/balance', true);
  if (!response.success || !response.data) return null;
  const data = response.data as { balance?: unknown; data?: { balance?: unknown } };
  const value = Number(data.balance ?? data.data?.balance);
  return Number.isFinite(value) ? value : null;
}

const getInitials = (name: string) =>
  name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('') || 'NA';

export default function PendingTransactionFloater() {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  /** Tab + panel: a press anywhere else closes the panel (the tab itself toggles it). */
  const panelRef = useRef<HTMLDivElement | null>(null);
  const lastFetchAtRef = useRef<number>(0);
  const isFetchingRef = useRef(false);
  const [pendingTransactions, setPendingTransactions] = useState<PendingDeposit[]>([]);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [brokenIcons, setBrokenIcons] = useState<Record<string, boolean>>({});
  /** A guided Add money whose payment landed: offered here until the player finishes it. */
  const [finishOffer, setFinishOffer] = useState<GuidedAddIntent | null>(null);
  const isPanelOpenRef = useRef(isPanelOpen);
  isPanelOpenRef.current = isPanelOpen;
  const finishOfferRef = useRef(finishOffer);
  finishOfferRef.current = finishOffer;

  /** Offer "Finish adding money" when a stored guided intent is now covered by My money. */
  const checkFinishOffer = useCallback(async () => {
    const intent = readGuidedAddIntent();
    if (!intent || intent.targets.length === 0 || !apiService.getAccessToken()) {
      setFinishOffer(null);
      return;
    }
    try {
      const available = await fetchAvailableBalance();
      if (available !== null) setFinishOffer(canFinishGuidedAdd(intent, available) ? intent : null);
    } catch {
      // Keep the previous offer on network failures.
    }
  }, []);

  const isOnPaymentPage = useMemo(
    () => PAYMENT_ROUTE_MATCHERS.some((path) => location.pathname.startsWith(path)),
    [location.pathname]
  );

  const fetchPendingTransactions = useCallback(async () => {
    if (!apiService.getAccessToken()) {
      setPendingTransactions([]);
      return;
    }
    if (isFetchingRef.current) {
      return;
    }
    isFetchingRef.current = true;
    lastFetchAtRef.current = Date.now();

    try {
      const [latestPending] = await Promise.all([fetchPendingDeposits(), checkFinishOffer()]);
      setPendingTransactions(latestPending);
      if (latestPending.length === 0 && !finishOfferRef.current && isPanelOpenRef.current) {
        setIsPanelOpen(false);
      }
    } catch {
      // Keep previous state as-is on network failures.
    } finally {
      isFetchingRef.current = false;
    }
  }, [checkFinishOffer]);

  const fetchPendingTransactionsIfAllowed = useCallback(() => {
    const now = Date.now();
    if (now - lastFetchAtRef.current < USER_ACTION_FETCH_THROTTLE_MS) {
      return;
    }
    void fetchPendingTransactions();
  }, [fetchPendingTransactions]);

  useEffect(() => {
    void fetchPendingTransactions();
  }, [fetchPendingTransactions]);

  useEffect(() => {
    if (!isOnPaymentPage) {
      void fetchPendingTransactions();
    }
  }, [isOnPaymentPage, fetchPendingTransactions]);

  useEffect(() => {
    const handleUserAction = () => {
      fetchPendingTransactionsIfAllowed();
    };

    const handleVisibilityOrFocus = () => {
      if (document.visibilityState === 'visible') {
        fetchPendingTransactionsIfAllowed();
      }
    };

    window.addEventListener('pointerdown', handleUserAction);
    window.addEventListener('keydown', handleUserAction);
    window.addEventListener('touchstart', handleUserAction);
    window.addEventListener('focus', handleVisibilityOrFocus);
    document.addEventListener('visibilitychange', handleVisibilityOrFocus);

    return () => {
      window.removeEventListener('pointerdown', handleUserAction);
      window.removeEventListener('keydown', handleUserAction);
      window.removeEventListener('touchstart', handleUserAction);
      window.removeEventListener('focus', handleVisibilityOrFocus);
      document.removeEventListener('visibilitychange', handleVisibilityOrFocus);
    };
  }, [fetchPendingTransactionsIfAllowed]);

  // A payment confirmed or money moved: the offer may now apply (or be gone).
  useEffect(() => {
    const onBalanceRefresh = () => void checkFinishOffer();
    window.addEventListener(BALANCE_REFRESH_EVENT, onBalanceRefresh);
    return () => window.removeEventListener(BALANCE_REFRESH_EVENT, onBalanceRefresh);
  }, [checkFinishOffer]);

  // Cancelled here or anywhere else in the app: drop the row at once.
  useEffect(() => {
    const onCancelled = (event: Event) => {
      const transactionId = (event as CustomEvent<DepositCancelledDetail>).detail?.transactionId;
      if (!transactionId) return;
      setPendingTransactions((prev) => {
        const next = prev.filter((tx) => tx.transactionId !== transactionId && tx.id !== transactionId);
        if (next.length === 0 && !finishOfferRef.current) setIsPanelOpen(false);
        return next;
      });
    };
    window.addEventListener(DEPOSIT_CANCELLED_EVENT, onCancelled);
    return () => window.removeEventListener(DEPOSIT_CANCELLED_EVENT, onCancelled);
  }, []);

  // A tap outside only closes the panel (the floater used to have a backdrop for this).
  useOutsidePressDismiss(panelRef, isPanelOpen, () => setIsPanelOpen(false));

  useEffect(() => {
    if (isOnPaymentPage && isPanelOpen) {
      setIsPanelOpen(false);
    }
  }, [isOnPaymentPage, isPanelOpen]);

  const handleResumeTransaction = (tx: PendingDeposit) => {
    playClickSound();
    setIsPanelOpen(false);
    resumePendingDeposit(tx, navigate);
  };

  const handleFinishAdding = () => {
    setIsPanelOpen(false);
    navigate('/money/add?resume=1');
  };

  if ((pendingTransactions.length === 0 && !finishOffer) || isOnPaymentPage) {
    return null;
  }

  const pendingCount = pendingTransactions.length + (finishOffer ? 1 : 0);
  const countLabel = t('transactions.pendingFloater.count', { count: pendingCount });
  const finishGameName = (() => {
    if (!finishOffer) return '';
    if (finishOffer.targets.length !== 1) return t('money.finish.yourGames');
    const id = finishOffer.targets[0].gameProviderId;
    return getCachedProvidersSync()?.data?.items.find((p) => p.id === id)?.name ?? t('money.finish.yourGame');
  })();

  // Floating card docked to the left edge (the shared edge dock, z-floating; the side strip takes
  // the right edge): rendered in place, no portal and no backdrop.
  return (
    <div className={EDGE_DOCK_CLASS.left}>
      <div ref={panelRef} className="pointer-events-auto flex items-center">
        <button
          type="button"
          onClick={() => {
            playClickSound();
            setIsPanelOpen((open) => !open);
          }}
          className={cx(
            'group h-17 w-11 overflow-hidden rounded-r-container border-y border-r border-edge bg-raised shadow-raised',
            'transition-[width] duration-(--k-dur-base) ease-(--k-ease) hover:w-37',
            focusRing
          )}
          aria-label={countLabel}
          aria-expanded={isPanelOpen}
          aria-controls="pending-transactions-panel"
        >
          <span className="flex h-full items-center gap-2 pl-3 pr-4">
            <span className="relative inline-flex" aria-hidden>
              <Loader2 className="size-5 animate-spin text-warning motion-reduce:animate-none" />
              <span className="absolute -right-3 -top-2 inline-flex h-5 min-w-5 animate-pulse items-center justify-center rounded-full bg-warning px-1 font-ui text-small font-semibold leading-none text-on-warning tabular-nums motion-reduce:animate-none">
                {pendingCount}
              </span>
            </span>
            <span
              aria-hidden
              className="max-w-0 whitespace-nowrap font-ui text-small text-primary opacity-0 transition-all duration-(--k-dur-base) ease-(--k-ease) group-hover:max-w-25 group-hover:opacity-100"
            >
              {countLabel}
            </span>
          </span>
        </button>

        {isPanelOpen && (
          <aside
            id="pending-transactions-panel"
            aria-labelledby="pending-transactions-title"
            className="cosmos-pop-in cosmos-pop-in--tl ml-2 flex max-h-[78vh] w-88 max-w-[calc(100vw-3.5rem)] flex-col overflow-hidden rounded-container border border-edge bg-raised shadow-raised"
          >
            <header className="flex items-start justify-between gap-3 border-b border-edge-subtle px-4 py-3.5">
              <div className="min-w-0">
                <h3 id="pending-transactions-title" className="m-0 font-ui text-body font-semibold text-primary">
                  {t('transactions.pendingFloater.title')}
                </h3>
                <p className="m-0 mt-0.5 font-ui text-small text-tertiary">
                  {t('transactions.pendingFloater.subtitle')}
                </p>
              </div>
              <IconButton
                label={t('common.close')}
                icon={<X />}
                size="sm"
                variant="ghost"
                silent
                onClick={() => setIsPanelOpen(false)}
              />
            </header>

            {finishOffer ? (
              <div className="border-b border-edge-subtle p-3">
                <Alert
                  tone="success"
                  title={t('money.finish.title')}
                  action={
                    <Button variant="primary" size="lg" fullWidth icon={<ArrowRight />} onClick={handleFinishAdding}>
                      {t('money.finish.action')}
                    </Button>
                  }
                >
                  {t('money.finish.body', { amount: formatUsd(guidedAddIntentTotal(finishOffer)), name: finishGameName })}
                </Alert>
              </div>
            ) : null}

            {pendingTransactions.length > 0 ? (
              <ul className="cosmos-scroll-y m-0 flex min-h-0 flex-1 list-none flex-col gap-1 p-2">
                {pendingTransactions.map((tx) => {
                  const iconPath = `/assets/games/${tx.platformSlug}/icon.png`;
                  const showIconFallback = brokenIcons[tx.id] || tx.isWalletDeposit;
                  return (
                    <li key={tx.id} className="flex flex-col">
                      <button
                        type="button"
                        onClick={() => handleResumeTransaction(tx)}
                        className={cx(
                          'grid w-full grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3 rounded-control px-3 py-3 text-left transition-colors duration-(--k-dur-fast) hover:bg-hover',
                          focusRing
                        )}
                      >
                        {!showIconFallback ? (
                          <img
                            src={iconPath}
                            alt={tx.platformName}
                            className="size-9 rounded-control border border-edge-subtle bg-inset object-cover"
                            onError={() => setBrokenIcons((prev) => ({ ...prev, [tx.id]: true }))}
                          />
                        ) : (
                          <span className="inline-flex size-9 items-center justify-center rounded-control border border-action-primary/36 bg-action-primary/14 text-amount-in">
                            {tx.isWalletDeposit ? (
                              <Wallet className="size-4" aria-hidden />
                            ) : (
                              <span className="font-ui text-small font-semibold">{getInitials(tx.platformName)}</span>
                            )}
                          </span>
                        )}
                        <span className="flex min-w-0 flex-col gap-0.5">
                          <span className="truncate font-ui text-body font-medium text-primary">
                            {tx.isWalletDeposit ? t('transactions.type.walletDeposit') : tx.platformName}
                          </span>
                          <span className="truncate font-ui text-small text-tertiary">
                            {tx.isWalletDeposit
                              ? t('transactions.pendingFloater.viaWallet')
                              : t('transactions.pendingFloater.viaQr')}{' '}
                            · {formatPendingTimeAgo(tx.createdAt, t)}
                          </span>
                        </span>
                        <span className="flex flex-col items-end gap-1">
                          <Money amount={tx.amount} direction="in" />
                          <StatusBadge domain="transaction" status={tx.status} label={t('transactions.status.pending')} size="sm" />
                        </span>
                      </button>
                      {/* Only a wallet top-up can be cancelled by the Player (not wallet → game). */}
                      {tx.isWalletDeposit && tx.transactionId ? (
                        <div className="flex justify-end px-3 pb-2">
                          <CancelDepositControl transactionId={tx.transactionId} size="sm" quiet />
                        </div>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            ) : null}
          </aside>
        )}
      </div>
    </div>
  );
}
